<script setup lang="ts">
/**
 * The rendered still on a catalogue card.
 *
 * Deliberately thin: it owns *when* a preview is asked for and what is shown
 * while it is coming, and nothing about how one is made. The rendering, the
 * queue and the cache all live in `model-thumbnail.ts` — see the header there
 * for why forty cards share one engine and end up holding none.
 *
 * The image is requested lazily, when the card first comes near the viewport.
 * Both tabs' worth of cards exist in the DOM the moment their tab is opened, so
 * without this the grid would render the whole roster on the way past even
 * though the player is looking at three of them.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { previewsAllowed } from '@/state/settings';

import type { Framing } from './model-thumbnail';

const props = defineProps<{
  /** The hull to draw. Null renders the fallback chassis. */
  hullId: string | null;
  /** The turret to draw. Null renders the fallback gun. */
  turretId: string | null;
  framing: Framing;
  /**
   * The livery to paint the tank in. Only read by the `'livery'` framing.
   *
   * Optional so the three existing framings' call sites are unchanged — a hull
   * card has no opinion about paint, and passing `null` from every one of them
   * to say so would be noise.
   */
  paintId?: string | null;
  /** For screen readers, and for the alt text if the render never arrives. */
  label: string;
  /**
   * Render on mount instead of waiting for the intersection observer.
   *
   * For the handful of cards that must be drawn the moment their tab opens
   * whatever the viewport — the shop's featured bundles, whose hero sizing can
   * push the third one past the observer's look-ahead margin. A bundle whose
   * tank never renders is the defect the shop's own comments warn about, so
   * those cards opt out of laziness rather than trusting the fold. Leave unset
   * everywhere a grid is genuinely long — laziness is the default for a reason.
   */
  eager?: boolean;
}>();

const root = ref<HTMLElement | null>(null);
const src = ref('');
const failed = ref(false);
/** True once this card has been close enough to the viewport to be worth drawing. */
const wanted = ref(false);

let observer: IntersectionObserver | null = null;

async function load(): Promise<void> {
  // Imported here rather than at the top of the file so Babylon is not on the
  // garage's critical path. Statically imported it cost 1.7 s before the first
  // card appeared — the renderer is several hundred kilobytes, and none of it is
  // needed to draw a price, a name and a stat bar. This way the shop paints
  // first and the stills arrive into it.
  try {
    const { thumbnail } = await import('./model-thumbnail');
    const url = await thumbnail(
      props.hullId,
      props.turretId,
      props.framing,
      props.paintId ?? null,
    );
    if (url) {
      src.value = url;
      failed.value = false;
    } else {
      failed.value = true;
    }
  } catch {
    // The module itself failed to arrive — offline, a chunk that 404s after a
    // deploy. `thumbnail` swallows its own render failures, so this is only ever
    // the import. A card with no picture is a smaller loss than an unhandled
    // rejection on every tile in the grid.
    failed.value = true;
  }
}

onMounted(() => {
  // Opted out: draw nothing and fetch nothing. The point of the setting is to
  // save the download as well as the render, so this has to be checked before
  // the observer rather than inside the render path.
  if (!previewsAllowed()) return;
  if (props.eager || typeof IntersectionObserver === 'undefined') {
    wanted.value = true;
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      wanted.value = true;
      observer?.disconnect();
      observer = null;
    },
    // A screen ahead, so a card is drawn before it is scrolled to rather than
    // after — the render is off the main thread's critical path either way, but
    // arriving early is the difference between a preview and a pop-in.
    { rootMargin: '600px' },
  );
  if (root.value) observer.observe(root.value);
});

onBeforeUnmount(() => observer?.disconnect());

watch(
  () => [wanted.value, props.hullId, props.turretId, props.framing, props.paintId] as const,
  ([ready]) => {
    if (ready) void load();
  },
  { immediate: true },
);
</script>

<template>
  <div ref="root" class="preview" :class="{ pending: !src }">
    <img v-if="src" :src="src" :alt="label" loading="lazy" decoding="async" />
    <span v-else-if="failed" class="fallback">{{ label }}</span>
    <span v-else class="fallback" aria-hidden="true"></span>
  </div>
</template>

<style scoped>
.preview {
  position: relative;
  /* A fixed box, held whether or not the image has arrived: a card that grows
     when its preview lands would reflow the whole grid mid-scroll. */
  aspect-ratio: 8 / 5;
  border-radius: 3px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 50% 78%, rgba(232, 114, 44, 0.09), transparent 62%),
    linear-gradient(180deg, var(--steel-800), var(--steel-900));
  border: 1px solid var(--steel-700);
}

.preview.pending::after {
  /* Faint sweep while the roster warms up, so an empty plate reads as "coming"
     rather than "broken". */
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, transparent 40%, rgba(255, 255, 255, 0.05) 50%, transparent 60%);
  animation: sweep 1.4s linear infinite;
}

@keyframes sweep {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}

img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.fallback {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 0.5rem;
  color: var(--steel-600);
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .preview.pending::after {
    animation: none;
  }
}
</style>
